import React from "react";
import {
  FileText,
  Code,
  Lightbulb,
  ExternalLink,
  Play,
  MessageSquare,
  Plus,
} from "lucide-react";
import PageHeader from "../../../components/marketing/cloud-dashboard/header";
import { useNavigate } from "react-router-dom";

interface SetupStep {
  number: number;
  text: string;
}

interface Feature {
  text: string;
}

interface Resource {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const RcsSetupPage: React.FC = () => {
    const navigate = useNavigate()
  const setupSteps: SetupStep[] = [
    { number: 1, text: "Verify your business with Google" },
    { number: 2, text: "Set up RCS agent account" },
    { number: 3, text: "Configure message templates" },
    { number: 4, text: "Test message delivery" },
  ];

  const keyFeatures: Feature[] = [
    { text: "Rich media messaging (images, carousels, videos)" },
    { text: "Suggested actions and quick replies" },
    { text: "Brand verification and messaging" },
    { text: "Read receipts and typing indicators" },
  ];

    function onBack() {
    navigate("/cpas-dashboard");
  }

  function onActionButtonClick() {
    navigate("/campaigns/create");
  }

  const resources: Resource[] = [
    {
      icon: <FileText className="w-6 h-6 text-gray-600" />,
      title: "Documentation",
      description: "Complete setup guide",
    },
    {
      icon: <Code className="w-6 h-6 text-gray-600" />,
      title: "API Reference",
      description: "Developer integration docs",
    },
    {
      icon: <Lightbulb className="w-6 h-6 text-yellow-500" />,
      title: "Best Practices",
      description: "Tips for better engagement",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 ">
      <PageHeader
        title="Rich Communication Services (RCS)"
        subtitle="Next-generation messaging with rich media and interactive features Create Campaign"
       
        onBack={onBack}
        actionButtonText="Create Campaign"
        onActionButtonClick={onActionButtonClick}
        actionButtonIcon={<Plus size={16} />}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Rich Communication Services (RCS)
          </h1>
          <p className="text-gray-600 mb-4">
            Next-generation messaging with rich media and interactive features
          </p>
          <div className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
            Setup Required
          </div>
        </div>

        {/* Main Setup Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Get Started with Rich Communication Services (RCS)
          </h2>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Setup Requirements:
            </h3>
            <div className="space-y-3">
              {setupSteps.map((step) => (
                <div key={step.number} className="flex items-center">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0">
                    {step.number}
                  </div>
                  <span className="text-gray-700">{step.text}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200">
            Start RCS Setup
          </button>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Need Help?
            </h4>
            <div className="flex flex-wrap gap-4 text-sm">
              <a
                href="#"
                className="flex items-center text-blue-600 hover:text-blue-700"
              >
                <FileText className="w-4 h-4 mr-1" />
                View Documentation
              </a>
              <a
                href="#"
                className="flex items-center text-blue-600 hover:text-blue-700"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Contact Support
              </a>
              <a
                href="#"
                className="flex items-center text-blue-600 hover:text-blue-700"
              >
                <Play className="w-4 h-4 mr-1" />
                Watch Tutorial
              </a>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Key Features */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Key Features
            </h3>
            <ul className="space-y-3">
              {keyFeatures.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700 text-sm">{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Resources
            </h3>
            <div className="space-y-4">
              {resources.map((resource, index) => (
                <div key={index} className="flex items-start">
                  <div className="mr-3 mt-1">{resource.icon}</div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      {resource.title}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {resource.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Need Support?
          </h3>
          <p className="text-gray-600 mb-4 text-sm">
            Our team is here to help you get started
          </p>
          <button className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default RcsSetupPage;
